import React from "react";
import { RelabelCaseColumnHeader } from "src/enums";
import { RelabelCaseRecord, RelabelCaseTrackingTabKey, RoutingActions, TableColumn } from "src/types";
import { RELABELLED_CASE_STATUSES } from "src/constants/relabelCases";
import { Table } from "src/components";
import { ActionCell } from "./ActionCell";
import { AnimalBadge } from "./AnimalBadge";
import { AnimalNonCertifiedBadge } from "./AnimalNonCertifiedBadge";
import { RouteToPill } from "./RouteToPill";
import { RoutingActionCell } from "./RoutingActionCell";
import { RoutingTypeBadge } from "./RoutingTypeBadge";
import { StatusPill } from "./StatusPill";

interface RelabelCasesTrackingTableProps {
  cases: RelabelCaseRecord[];
  tab: RelabelCaseTrackingTabKey;
  onAction?: (case_: RelabelCaseRecord) => void;
  onEditQtyAction?: (case_: RelabelCaseRecord) => void;
  routingActions?: RoutingActions;
  loadingCaseIds?: Set<string>;
}

const buildBaseColumns = (): TableColumn<RelabelCaseColumnHeader, RelabelCaseRecord>[] => [
  {
    header: RelabelCaseColumnHeader.CASE_ID,
    column: (row) => <span className="font-mono text-sm">{row.id}</span>,
    styles: { whiteSpace: "nowrap" }
  },
  {
    header: RelabelCaseColumnHeader.PURCHASE_ORDER,
    column: (row) => row.purchaseOrderId ?? row.transferOrderId ?? "—",
    styles: { whiteSpace: "nowrap" }
  },
  {
    header: RelabelCaseColumnHeader.VENDOR,
    column: (row) => row.vendorName ?? ""
  },
  {
    header: RelabelCaseColumnHeader.TYPE,
    column: (row) => (
      <div className="flex items-center justify-center gap-1.5">
        <RoutingTypeBadge routingType={row.routingType} />
        {row.isAnimalSku && <AnimalBadge />}
        {row.isAnimalNonCertified && <AnimalNonCertifiedBadge />}
      </div>
    ),
    styles: { textAlign: "center", minWidth: "180px" }
  },
  {
    header: RelabelCaseColumnHeader.CONTENTS,
    column: (row) => (
      <div className="flex flex-wrap gap-1.5 py-1">
        {row.products.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-secondary-50 px-2 py-1"
          >
            <span className="font-mono text-xs font-bold text-secondary-900">{p.skuName}</span>
            <span className="text-secondary-300 select-none">×</span>
            <span className="text-xs font-semibold text-secondary-600">{p.quantity}</span>
          </span>
        ))}
      </div>
    ),
    styles: { minWidth: "220px" }
  },
  {
    header: RelabelCaseColumnHeader.UNITS,
    column: (row) => <span className="font-semibold tabular-nums">{row.totalUnits}</span>,
    styles: { textAlign: "center", width: "72px" }
  }
];

const buildRoutingTabColumns = (
  routingActions: RoutingActions | undefined
): TableColumn<RelabelCaseColumnHeader, RelabelCaseRecord>[] => [
  {
    header: RelabelCaseColumnHeader.ROUTE_TO,
    column: (row) => (
      <div className="flex justify-center">
        <RouteToPill case_={row} />
      </div>
    ),
    styles: { textAlign: "center", minWidth: "230px" }
  },
  {
    header: RelabelCaseColumnHeader.ACTIONS,
    column: (row) => (routingActions ? <RoutingActionCell case_={row} actions={routingActions} /> : <></>),
    styles: { textAlign: "right", paddingRight: "24px", minWidth: "260px" }
  }
];

const buildStandardActionColumn = (
  tab: RelabelCaseTrackingTabKey,
  showStandardAction: boolean,
  onAction: ((case_: RelabelCaseRecord) => void) | undefined,
  onEditQtyAction: ((case_: RelabelCaseRecord) => void) | undefined,
  loadingCaseIds: Set<string> | undefined
): TableColumn<RelabelCaseColumnHeader, RelabelCaseRecord> => ({
  header: showStandardAction ? RelabelCaseColumnHeader.ACTIONS : RelabelCaseColumnHeader.STATUS,
  column: (row) =>
    showStandardAction && onAction ? (
      <ActionCell
        case_={row}
        tab={tab}
        isLoading={loadingCaseIds?.has(row.id) ?? false}
        onAction={onAction}
        onEditQtyAction={onEditQtyAction}
      />
    ) : (
      <div className="flex flex-col items-end gap-1">
        <StatusPill status={row.status} />
        {row.status === "COMPLETE" && row.routedLocationId && <RouteToPill case_={row} variant="pending" />}
      </div>
    ),
  styles: { textAlign: "right", paddingRight: "24px", minWidth: "220px" }
});

const outputColumn: TableColumn<RelabelCaseColumnHeader, RelabelCaseRecord> = {
  header: RelabelCaseColumnHeader.OUTPUT,
  column: (row) =>
    RELABELLED_CASE_STATUSES.has(row.status) ? (
      <div className="flex flex-wrap gap-1.5 py-1">
        {row.products.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1"
          >
            <span className="font-mono text-xs font-bold text-emerald-900">{p.euSkuName}</span>
            <span className="text-emerald-300 select-none">×</span>
            <span className="text-xs font-semibold text-emerald-700">{p.quantity}</span>
          </span>
        ))}
      </div>
    ) : (
      <span className="text-xs text-secondary-300">—</span>
    ),
  styles: { minWidth: "220px" }
};

const RelabelCasesTrackingTable: React.FC<RelabelCasesTrackingTableProps> = ({
  cases,
  tab,
  onAction,
  onEditQtyAction,
  routingActions,
  loadingCaseIds
}) => {
  const isRoutingTab = tab === "routing";
  const showStandardAction = tab !== "all" && !isRoutingTab;
  const showOutput = tab === "all";

  const columns: TableColumn<RelabelCaseColumnHeader, RelabelCaseRecord>[] = [
    ...buildBaseColumns(),
    ...(isRoutingTab
      ? buildRoutingTabColumns(routingActions)
      : [buildStandardActionColumn(tab, showStandardAction, onAction, onEditQtyAction, loadingCaseIds)]),
    ...(showOutput ? [outputColumn] : [])
  ];

  return <Table<RelabelCaseColumnHeader, RelabelCaseRecord> data={cases} columns={columns} />;
};

export { RelabelCasesTrackingTable };
